import React, {useContext, useEffect, useState} from "react";
import {Button, Col, Form, Row} from "react-bootstrap";
import {useIntl} from "react-intl";
import messages from "../../../i18n/messages";
import {SelectedDateContext} from "../../../contexts/date/SelectedDateContext";
import {getLocale} from "../../../contexts/language/LanguageContext";
import {
    confirmDailyCheckIn,
    doDailyCheckIn,
    getAvailableCheckInItems,
    getDailyCheckInItems,
    validateCheckInDate
} from "../../../utils/CheckInUtil";
import {AuthContext} from "../../../contexts/auth/AuthContext";
import {LoadingContext} from "../../../contexts/loading/LoadingContext";
import {ModalContext} from "../../../contexts/modal/ModalContext";
import './DailyCheckInStatus.css';
import MaxiIcon from "../../Maxiicon/MaxiIcon";

const DailyCheckInStatus = () => {
    const intl = useIntl();
    const [selectedDateContext,] = useContext(SelectedDateContext);
    const [authContext,] = useContext(AuthContext);
    const [, setModalContext] = useContext(ModalContext);
    const [, setLoadingContext] = useContext(LoadingContext);
    const [checkInData, setCheckInData] = useState({
        availableCheckInItems: [],
        dailyCheckInData: undefined,
        checkedItems: [],
    });

    const getDateStr = () => {
        return selectedDateContext.getFullYear() + '-' + (selectedDateContext.getMonth() + 1) + '-'
            + selectedDateContext.getDate();
    }

    async function getCheckInData() {
        if (selectedDateContext && authContext.token) {
            try {
                setLoadingContext({
                    isLoading: true,
                    loadingText: '',
                });
                const dateStr = getDateStr();
                const [availableCheckInItemsResp, dailyCheckInItemsResp] =
                    await Promise.all([getAvailableCheckInItems(authContext.token),
                        getDailyCheckInItems(dateStr, authContext.token)]);
                if (!availableCheckInItemsResp.ok || !dailyCheckInItemsResp.ok) {
                    if (availableCheckInItemsResp.status === 401 || dailyCheckInItemsResp.status === 401) {
                        setModalContext({
                            isShow: true,
                            title: intl.formatMessage(messages.errMsgTitle),
                            content: intl.formatMessage(messages.errMsgUnauthorized),
                            onConfirm: () => {},
                        });
                    } else {
                        setModalContext({
                            isShow: true,
                            title: intl.formatMessage(messages.errMsgTitle),
                            content: intl.formatMessage(messages.errMsgServerError),
                            onConfirm: () => {},
                        });
                    }
                } else {
                    let tmpDailyCheckInData = [];
                    let checkedItemIds = [];
                    if (dailyCheckInItemsResp.status !== 204) {
                        let dailyData = await dailyCheckInItemsResp.json();
                        tmpDailyCheckInData = dailyData;
                        checkedItemIds = dailyData.checkInItems.map(item => item.id);
                    }
                    let itemData = await availableCheckInItemsResp.json();
                    setCheckInData({dailyCheckInData: tmpDailyCheckInData,
                        availableCheckInItems: itemData,
                        checkedItems: checkedItemIds});
                }
            } catch (error) {
                console.log(error);
            } finally {
                setLoadingContext({
                    isLoading: false,
                    loadingText: '',
                });
            }
        }
    }

    async function onCheckInButtonClick() {
        const dateStr = getDateStr();
        const result = validateCheckInDate(dateStr, 7);
        if (result === 1) {
            setModalContext({
                isShow: true,
                title: intl.formatMessage(messages.errMsgTitle),
                content: intl.formatMessage(messages.errMsgCheckInFutureError),
                onConfirm: () => {},
            });
        } else if (result === -1) {
            setModalContext({
                isShow: true,
                title: intl.formatMessage(messages.errMsgTitle),
                content: intl.formatMessage(messages.errMsgCheckInExpiredError),
                onConfirm: () => {},
            });
        } else {
            if (selectedDateContext && authContext.token) {
                try {
                    setLoadingContext({
                        isLoading: true,
                        loadingText: '',
                    });

                    const response = await doDailyCheckIn(dateStr, checkInData.checkedItems, authContext.token);
                    if (!response.ok) {
                        console.log(response);
                        setModalContext({
                            isShow: true,
                            title: intl.formatMessage(messages.errMsgTitle),
                            content: intl.formatMessage(messages.errMsgServerError),
                            onConfirm: () => {},
                        });
                    } else {
                        let dailyData = await response.json();
                        setCheckInData({...checkInData, dailyCheckInData: dailyData});
                    }
                } catch (error) {
                    console.log(error);
                } finally {
                    setLoadingContext({
                        isLoading: false,
                        loadingText: '',
                    });
                }
            }
        }
    }

    async function onConfirmButtonClick(isConfirmed) {
        if (selectedDateContext && authContext.token) {
            try {
                setLoadingContext({
                    isLoading: true,
                    loadingText: '',
                });
                const dateStr = getDateStr();
                const response = await confirmDailyCheckIn(dateStr, isConfirmed, authContext.token);
                if (!response.ok) {
                    setModalContext({
                        isShow: true,
                        title: intl.formatMessage(messages.errMsgTitle),
                        content: intl.formatMessage(messages.errMsgServerError),
                        onConfirm: () => {},
                    });
                } else {
                    let dailyData = await response.json();
                    setCheckInData({...checkInData, dailyCheckInData: dailyData});
                }
            } catch (error) {
                console.log(error);
            } finally {
                setLoadingContext({
                    isLoading: false,
                    loadingText: '',
                });
            }
        }
    }


    const renderDailyCheckIn = () => {
        if (checkInData.dailyCheckInData && checkInData.dailyCheckInData.checkInItems
            && checkInData.dailyCheckInData.checkInItems.length !== 0) {
            let classStr = checkInData.dailyCheckInData.isConfirmed ? 'confirmed-item' : 'not-confirmed-item';
            let checkInText = checkInData.dailyCheckInData.isConfirmed ? <></> : <div className={'not-confirmed-text'}>{intl.formatMessage(messages.checkInNotConfirmedText)}</div> ;
            return (
                <>
                    <hr/>
                    <br/>
                    <Row>
                        <Col xs={12} sm={12} md={{span: 4, offset: 4}} className={'check-in-status-header'}>
                            {intl.formatMessage(messages.todayCheckedInLabel)}
                        </Col>
                    </Row>
                    <Row>
                        {checkInData.dailyCheckInData.checkInItems.map((item, index) => {
                            return (
                                <Col xs={12} key={index}>
                                    {getItemName(item)}
                                    <MaxiIcon iconName={'faCheck'} className={'daily-checked-item'}/>
                                    <MaxiIcon iconName={'faUserCheck'} className={classStr}/>
                                </Col>
                            );
                        })}
                    </Row>
                    <br/>
                    {authContext.isAdmin &&
                    <Row>
                        <Col xs={12} sm={12} md={{span: 4, offset: 2}}>
                            <Button variant={'success'} onClick={() => onConfirmButtonClick(true)}>
                                <MaxiIcon iconName={'faUserCheck'}/>&nbsp;{intl.formatMessage(messages.confirmButtonLabel)}
                            </Button>
                        </Col>
                        <Col xs={12} sm={12} md={4}>
                            <Button variant={'secondary'} onClick={() => onConfirmButtonClick(false)}>
                                <MaxiIcon iconName={'faUserCheck'}/>&nbsp;{intl.formatMessage(messages.cancelConfirmButtonLabel)}
                            </Button>
                        </Col>
                    </Row>
                    }
                    {checkInText}
                    <hr/>
                </>

            );
        }
    }

    const onCheckInCheckBoxClick = (event, itemId) => {
        let checkedItemIds = checkInData.checkedItems;
        if (event.target.checked) {
            if (!checkedItemIds.includes(itemId)) {
                checkedItemIds.push(itemId);
            }
        } else {
            if (checkedItemIds.includes(itemId)) {
                checkedItemIds = checkedItemIds.filter(id => id !== itemId);
            }
        }
        setCheckInData({...checkInData, checkedItems: checkedItemIds});
    }

    const getCheckedStatus = (itemId) => {
        return checkInData.checkedItems.includes(itemId);
    }

    const getItemName = (item) => {
        if (messages[item.itemNameStr]) {
            return <span><MaxiIcon iconName={item.itemIconName} />&nbsp;{intl.formatMessage(messages[item.itemNameStr])}</span>;
        } else {
            return <span><MaxiIcon iconName={item.itemIconName} />&nbsp;{item.itemName}</span>;
        }
    }

    const renderAvailableCheckInItems = () => {
        if (checkInData.availableCheckInItems.length !== 0) {
            return (
                <>
                    <Row>
                        {checkInData.availableCheckInItems.map((item, index) => {
                            return (
                                <Col key={index} xs={12} md={4}>
                                    <Form.Check
                                        type={'checkbox'}
                                        id={'availableCheckInItem' + item.id}
                                        label={getItemName(item)}
                                        onChange={(e) => onCheckInCheckBoxClick(e, item.id)}
                                        checked={getCheckedStatus(item.id)}
                                    />
                                </Col>
                            );
                        })}
                    </Row>
                    <br/>
                    <Row>
                        <Col xs={12} sm={12} md={{span: 4, offset: 4}}>
                            <Button onClick={onCheckInButtonClick}>
                                <MaxiIcon iconName={'faCalendarCheck'}/>&nbsp;{intl.formatMessage(messages.checkInButtonLabel)}
                            </Button>
                        </Col>
                    </Row>
                    <br/>
                </>
            );
        } else {
            return <></>;
        }
    }

    useEffect(() => {
        getCheckInData();
    }, [selectedDateContext]);

    return (
        <Row>
            <Col xs={12} className={'check-in-status-header'}>
                {intl.formatMessage(messages.checkInDateLabel) + ': ' + selectedDateContext.toLocaleDateString(getLocale(intl.locale))}
            </Col>
            <Col>
                {renderAvailableCheckInItems()}
                {renderDailyCheckIn()}
            </Col>
        </Row>
    );
}

export default DailyCheckInStatus;